import React from "react";
import PropTypes from "prop-types";
import useListbox from "../../hooks/use-listbox";
import Listbox from "./listbox";
import ListboxSelectItem from "./listbox-select-item";
import ListboxContent from "./listbox-content";
import ListboxGroup from "./listbox-group";
import Button from "../elements/button";

const ListboxSelect = ({
  options,
  onChange,
  value,
  emptyValue,
  onSearch,
  onClose = () => {},
  scrollFetch,
  loading = false,
  label = "",
  disabled = false,
  size,
  icon,
  buttonClassName,
  hideSelectedValue,
  headerText,
  headerClassName = "",
  children,
}) => {
  const { filteredOptions, flattenedOptions, handleSearch, handleClose, filteredCount, isGrouped, isSearchable } =
    useListbox(options, onSearch, onClose, scrollFetch);
  const selectedOption =
    value && (value.label ? value : flattenedOptions.find((option) => option.value === value.value));
  const displayValue = selectedOption ? selectedOption.label : "";
  const labelAndValue = selectedOption ? `${label}. ${displayValue} selected` : `${label}. No option selected`;

  return (
    <Listbox
      ariaLabel={labelAndValue}
      emptyValue={emptyValue}
      value={displayValue}
      size={size}
      disabled={disabled}
      onClose={handleClose}
      icon={icon}
      buttonClassName={buttonClassName}
      hideSelectedValue={hideSelectedValue}
    >
      {({ closePopover }) => (
        <>
          {headerText && <p className={`listbox-select--header ${headerClassName}`}>{headerText}</p>}
          <ListboxContent
            searchable={isSearchable}
            filteredCount={filteredCount}
            handleSearch={handleSearch}
            scrollFetch={scrollFetch}
            loading={loading}
          >
            {filteredOptions.map((option) => {
              return isGrouped ? (
                <ListboxGroup key={option.label} label={option.label} showLabel={option.items.length > 0}>
                  {option.items.map((option) => (
                    <ListboxSelectItem
                      key={option.value}
                      onClick={onChange}
                      item={option}
                      selectedValue={value}
                      closePopover={closePopover}
                    />
                  ))}
                </ListboxGroup>
              ) : (
                <ListboxSelectItem
                  key={option.value}
                  onClick={onChange}
                  item={option}
                  selectedValue={value}
                  closePopover={closePopover}
                />
              );
            })}
            {children}
          </ListboxContent>
        </>
      )}
    </Listbox>
  );
};

const optionsFormat = PropTypes.arrayOf(
  PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.any,
    icon: PropTypes.string,
    disabled: PropTypes.bool,
  })
);
const groupedOptionsFormat = PropTypes.arrayOf(
  PropTypes.shape({
    label: PropTypes.string.isRequired,
    items: optionsFormat.isRequired,
  })
);

ListboxSelect.propTypes = {
  options: PropTypes.oneOfType([optionsFormat, groupedOptionsFormat]).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  /**
   * You can pass an emptyValue if you want to display something other than the default "Select..."
   */
  emptyValue: PropTypes.string,
  onSearch: PropTypes.func,
  onClose: PropTypes.func,
  scrollFetch: PropTypes.func,
  loading: PropTypes.bool,
  value: PropTypes.any,
  disabled: PropTypes.bool,
  size: PropTypes.string,
  icon: PropTypes.string,
  buttonClassName: PropTypes.string,
  hideSelectedValue: PropTypes.bool,
  headerText: PropTypes.string,
  headerClassName: PropTypes.string,
  children: PropTypes.object,
};

export default ListboxSelect;
